/**
 * ENG-8346 — reading the direct-chat cursor-advance count, on every client.
 *
 * `POST /host/direct-chat/reply` and `/host/direct-chat/consume` flip the
 * claimed user messages to `delivered`. Both report how many rows actually
 * moved (`consumed`); `/reply` additionally reports WHY fewer moved than were
 * asked for (`reason`, ENG-8337). Until this module, no client read either:
 * all nine cursor-advancing call sites gated on `res.ok && data.error == null`,
 * which cannot tell "advanced" from "moved nothing".
 *
 * That distinction is the whole point. A cursor that does not advance leaves the
 * row `processing`, the stale-`processing` reaper reverts it to `pending`, and at
 * 30 minutes the give-up sweep tells the user to resend a message the agent has
 * already answered. ENG-8337 fixed the server predicate that caused it; this
 * module is how a host would ever KNOW it was happening.
 *
 * ## Why a verdict and not a helper that logs
 *
 * The nine sites differ in REACTION, not in JUDGEMENT. A tool handler returns
 * `{content, isError}` to the agent; an intercept branch writes stderr and evicts
 * a cooldown-cache entry; the recovery outbox returns `{ok, error}` to another
 * pure module; the opencode arm in the manager writes a `log()` line. A helper
 * that logged, or that returned `isError`, would import one site's conventions
 * into the other eight. So this returns a discriminated verdict and each site
 * maps verdict → effect. Same shape as the sibling decision modules
 * (`kanban-write-outcome.ts`, `usage-limit-reactive-decision.ts`).
 *
 * It absorbs the FAILURE branch as well as the shortfall branch, deliberately.
 * A module that only classified shortfalls would leave every site with two
 * checks — the existing `res.ok && data.error == null` plus a new one — which is
 * how the nine drifted apart in the first place.
 *
 * ## Lives in @augmented/core, not packages/mcp
 *
 * Eight sites are in `packages/mcp`, but the ninth (the ADR-0047 opencode arm)
 * is in `apps/cli`, which cannot import from the MCP package. Both depend on
 * `@augmented/core`. This file is kept free of node builtins so it can ride the
 * main barrel; the counter that records a shortfall needs `node:fs` and lives in
 * the `./direct-chat/cursor-advance-telemetry.js` subpath instead.
 */

/** The two routes that advance the direct-chat cursor. */
export type CursorAdvanceRoute = 'reply' | 'consume';

/**
 * The response body both routes return.
 *
 * Every field is optional because an MCP bundle outlives the API stage it talks
 * to — see the skew rule on `consumed` below.
 */
export interface CursorAdvanceResponseBody {
  ok?: boolean;
  error?: string;
  /**
   * Rows actually advanced. ABSENT means "this API stage did not report", which
   * is NOT zero — see `classifyCursorAdvance`.
   */
  consumed?: number;
  /** `/reply` only, and only on a shortfall (ENG-8337). */
  reason?: string;
}

/**
 * The normalised shortfall reason that reaches the counter.
 *
 * The routes supply the first seven (`classifyCursorShortfall`, ENG-8337 +
 * ENG-8348). The last three are added here:
 *
 * - `unreported`      — the route reported a count but no reason. Now emitted
 *                       only by an API stage predating ENG-8348, which is why
 *                       it must stay distinct from `unknown`: one means nobody
 *                       looked, the other means someone looked and found no
 *                       explanation.
 * - `malformed_count` — `consumed` was present but not a non-negative safe
 *                       integer. Cannot happen against today's API (the server
 *                       sends an array length); counted rather than ignored
 *                       because silently reading corruption as success is the
 *                       exact failure this module exists to end.
 * - `other`           — a reason string this build has never heard of.
 *
 * `other` is load-bearing. A newer API adding a seventh reason must still be
 * COUNTED, not dropped: dropping it would make the metric read a clean zero
 * meaning "never ingested" — the ENG-7716 / ENG-8214 failure, arriving through
 * the client instead of an allowlist. Folding it into a bounded bucket keeps the
 * CloudWatch dimension cardinality fixed while keeping the event visible.
 */
export type CursorShortfallReasonKey =
  | 'not_found'
  | 'unclaimed'
  | 'gave_up'
  | 'already_delivered'
  | 'error'
  | 'undiagnosed'
  | 'unknown'
  | 'unreported'
  | 'malformed_count'
  | 'other';

/**
 * ENG-8393 — the three NON-SHORTFALL outcomes, which are what give the metric a
 * positive control.
 *
 * Two of them (`advanced`, `advanced_unreported`) are benign; `failed` is not —
 * it records an advance that did not happen. "Non-shortfall" is what they
 * actually have in common, and an earlier revision of this constant was named
 * `..._BENIGN_KEYS`, which quietly asserted that a failed POST was a happy
 * outcome (CodeRabbit, PR #4023). What unites them is that each was previously
 * written NOWHERE, which is the defect.
 *
 * Until this ticket the counter was written only on a shortfall, so a
 * fleet-wide zero could not be told apart from a pipeline that never ingested
 * anything: "nothing went wrong" and "nothing was ever measured" produced
 * byte-identical state. That is the ENG-7716 / ENG-8214 clean-zero failure with
 * the counter itself as the blind spot, and it mattered because ENG-8347 reads
 * this metric to make a go/no-go call — a zero that means "never ingested"
 * reads as "no shortfalls, safe to proceed".
 *
 * The sibling counter avoids it and the reason is visible in its dimensions:
 * `SlackHotThreadClassifications` carries `Outcome=not_applicable`, i.e. it
 * records its benign outcome too. These are this metric's equivalent.
 *
 * - `advanced`            — every id asked for moved, and the API SAID so.
 * - `advanced_unreported` — the API stage did not report a count, so nothing was
 *                           compared. Kept distinct from `advanced` because it
 *                           is the fleet-skew case: an agent reporting only
 *                           these is running against an API that predates
 *                           `consumed`, and its zero shortfall count is
 *                           uninformative for exactly the same reason a missing
 *                           metric is. Collapsing the two would hide that.
 * - `failed`              — the POST itself failed. Counted (the original
 *                           ENG-8346 decision was NOT to) because a fleet in
 *                           which every advance fails would otherwise report
 *                           zero shortfalls AND zero successes, which is the
 *                           same unreadable zero one step removed. The row is
 *                           still redelivered; this changes no control flow.
 *
 * `partial` is always `false` for these three — it describes a MIXED shortfall
 * set and has no meaning off that arm.
 */
export const CURSOR_ADVANCE_NON_SHORTFALL_KEYS = ['advanced', 'advanced_unreported', 'failed'] as const;

export type CursorAdvanceNonShortfallKey = (typeof CURSOR_ADVANCE_NON_SHORTFALL_KEYS)[number];

/**
 * Everything that can occupy the reason slot of a counter key — the shortfall
 * reasons plus the three non-shortfall outcomes.
 */
export type CursorAdvanceOutcomeKey = CursorShortfallReasonKey | CursorAdvanceNonShortfallKey;

/**
 * Every key the reason slot can hold, in the order they appear above.
 *
 * Mirrored by the `KNOWN_CURSOR_SHORTFALL_REASONS` sets in the CLI probe reader
 * and the API probe handler. A value accepted by only one of those is dropped
 * with no error and no log — add to all three in the same change (enforced by
 * `scripts/check-classification-allowlist-parity.mjs`, which compares this
 * array against both allowlists for exact equality).
 *
 * The name predates ENG-8393 and is now slightly narrow: since that ticket this
 * is the reason-SLOT vocabulary, and its last three members are not shortfalls
 * at all. Kept rather than renamed because the name is load-bearing in the
 * guard's `TRIPLE_SOURCED` config and in both allowlists, and a rename buys
 * nothing a comment cannot. `normalizeCursorShortfallReason` below deliberately
 * excludes the benign members, so a server that sent `reason: 'advanced'` on a
 * genuine shortfall still normalises to `other`.
 */
export const CURSOR_SHORTFALL_REASONS: readonly CursorAdvanceOutcomeKey[] = [
  'not_found',
  // ENG-8348: `/consume` only. The row belongs to this agent+session but was
  // never claimed, so the caller is acking work it cannot have done — the one
  // case that route's widened predicate deliberately refuses to advance. It has
  // to be nameable or the refusal is unmeasurable.
  'unclaimed',
  'gave_up',
  'already_delivered',
  'error',
  'undiagnosed',
  'unknown',
  'unreported',
  'malformed_count',
  'other',
  // ENG-8393: the non-shortfall outcomes — two benign, plus `failed`. Written
  // on the paths that previously wrote nothing, so a zero on the shortfall
  // reasons above becomes readable — see CURSOR_ADVANCE_NON_SHORTFALL_KEYS.
  'advanced',
  'advanced_unreported',
  'failed',
];

const NON_SHORTFALL_KEYS = new Set<string>(CURSOR_ADVANCE_NON_SHORTFALL_KEYS);

/**
 * The SHORTFALL reasons only — `CURSOR_SHORTFALL_REASONS` minus the
 * non-shortfall outcomes. Derived rather than written out a second time so the
 * two cannot drift; pinned by a test.
 */
const KNOWN_REASONS = new Set<string>(
  CURSOR_SHORTFALL_REASONS.filter((k) => !NON_SHORTFALL_KEYS.has(k)),
);

/**
 * Map a server-supplied reason onto a bounded key.
 *
 * Absent ⇒ `unreported`; unrecognised ⇒ `other`. Never returns the raw string,
 * so a future API reason cannot become an unbounded CloudWatch dimension.
 */
export function normalizeCursorShortfallReason(raw: string | undefined | null): CursorShortfallReasonKey {
  if (raw == null || raw === '') return 'unreported';
  return KNOWN_REASONS.has(raw) ? (raw as CursorShortfallReasonKey) : 'other';
}

/**
 * What happened to a cursor advance.
 *
 * - `advanced` — every id the caller asked for moved, OR the API stage did not
 *   report a count. `reported` tells those apart; neither warns.
 * - `shortfall` — the API reported moving fewer rows than were asked for. The
 *   one outcome that was previously invisible.
 * - `failed` — the POST itself failed (non-2xx, or a 200 carrying `{error}`).
 *   Not silent: the row stays claimed and the next poll redelivers it.
 */
export type CursorAdvanceVerdict =
  | { outcome: 'advanced'; reported: boolean; expected: number; consumed: number }
  | {
      outcome: 'shortfall';
      expected: number;
      consumed: number;
      reason: CursorShortfallReasonKey;
      /** `consumed > 0`: some ids moved and some did not — a MIXED set. */
      partial: boolean;
    }
  | {
      outcome: 'failed';
      error: string;
      /**
       * How many DISTINCT ids the caller asked to advance. Carried on this arm
       * too (ENG-8393) so the no-op exclusion in `cursorAdvanceCounterKey` is
       * uniform across all three verdicts rather than holding only for the ones
       * that happened to have the field.
       */
      expected: number;
    };

/** Everything a caller knows about one cursor-advancing POST. */
export interface CursorAdvanceInput {
  /**
   * The ids sent as `message_ids`. DEDUPED here before counting, because
   * Postgres matches `IN (a, a)` exactly ONCE: comparing `consumed` against the
   * raw array length would report a shortfall on a fully successful call.
   * `/reply` dedupes server-side too (ENG-8337); `/consume` does not, so the
   * client dedupe is what makes the two routes agree.
   */
  messageIds: readonly string[];
  /**
   * Whether the HTTP call succeeded. Callers using the raw `fetch` in the MCP
   * pass `res.ok`; the manager's `api.post` THROWS on non-2xx, so its caller
   * only reaches here on success and passes `true`.
   */
  httpOk: boolean;
  httpStatus?: number | undefined;
  statusText?: string | undefined;
  /** The parsed response body, or undefined when it could not be parsed. */
  body?: CursorAdvanceResponseBody | null | undefined;
}

/**
 * Classify one cursor advance.
 *
 * THE SKEW RULE, which is the reason this is not a two-line comparison. The MCP
 * ships inside the agt-cli bundle and updates independently of the API deploy,
 * so a host running this build WILL talk to an API stage that predates
 * `consumed`. An absent field must therefore mean "not reported", never zero —
 * otherwise every reply on an older stage warns, and the operator learns to
 * ignore the warn before the real one ever arrives. The server omits the field
 * when no flip was attempted, so an explicit `0` always means "tried, moved
 * nothing". Same shape as the `required-request-field` skew note.
 */
export function classifyCursorAdvance(input: CursorAdvanceInput): CursorAdvanceVerdict {
  const body = input.body ?? undefined;

  // Failure first, and on the same predicate every site already used, so that
  // adopting this module cannot change which responses count as failures.
  if (!input.httpOk || body?.error != null) {
    const error =
      body?.error ??
      input.statusText ??
      (input.httpStatus !== undefined ? `HTTP ${input.httpStatus}` : 'request failed');
    return { outcome: 'failed', error, expected: new Set(input.messageIds).size };
  }

  const expected = new Set(input.messageIds).size;
  const raw = body?.consumed;

  // Skew rule: not reported ⇒ nothing to compare against. Never a shortfall.
  if (raw === undefined || raw === null) {
    return { outcome: 'advanced', reported: false, expected, consumed: 0 };
  }

  // A count that is not a non-negative safe integer is corruption, not a number
  // to reason about. Only meaningful when something was actually asked for.
  if (typeof raw !== 'number' || !Number.isSafeInteger(raw) || raw < 0) {
    if (expected === 0) return { outcome: 'advanced', reported: false, expected, consumed: 0 };
    return {
      outcome: 'shortfall',
      expected,
      consumed: 0,
      reason: 'malformed_count',
      partial: false,
    };
  }

  // `consumed > expected` is unreachable — the server filters `.in('id', ids)`,
  // so it cannot move a row the caller did not name. Treated as advanced rather
  // than asserted: a client has no business failing on a server that over-reports.
  if (raw < expected) {
    return {
      outcome: 'shortfall',
      expected,
      consumed: raw,
      reason: normalizeCursorShortfallReason(body?.reason),
      partial: raw > 0,
    };
  }

  return { outcome: 'advanced', reported: true, expected, consumed: raw };
}

/**
 * The counter key: `<route>|<reason>|<partial>`.
 *
 * All three parts are bounded enums (2 x 13 x 2 = 52), so the CloudWatch
 * dimension cardinality is fixed. None of the parts contains `|`.
 *
 * `partial` earns its place: a mixed set — one reaper-reverted id advancing
 * alongside one that did not — is the case that strands a message while
 * `consumed` is non-zero, and it is the case the deferred bookkeeping decision
 * (ENG-8347) turns on.
 */
export function cursorShortfallKey(
  route: CursorAdvanceRoute,
  reason: CursorAdvanceOutcomeKey,
  partial: boolean,
): string {
  return `${route}|${reason}|${partial ? 'true' : 'false'}`;
}

/**
 * ENG-8393 — the counter key for ANY verdict, or `null` when there is nothing
 * worth counting.
 *
 * This exists so a call site cannot record the wrong thing, or forget to record
 * a non-shortfall case. Before it, each site chose its own key inside its own
 * `outcome === 'shortfall'` branch — and every site making the same correct
 * choice is precisely how the metric ended up with no denominator: the branch
 * they all shared was the only one that wrote anything.
 *
 * Returns `null` for a verdict that asked for NOTHING (`expected` 0). Such a
 * call moves no cursor, so counting it would pad the denominator with no-ops and
 * make the shortfall RATE read lower than it is.
 *
 * That check is deliberately FIRST, ahead of every outcome (CodeRabbit, PR
 * #4023). An earlier revision applied it only on the `advanced` arm, which was
 * not a considered exemption — it was an accident of which verdict variants
 * happened to carry `expected`, and it left `failed` counting zero-id calls
 * while `advanced` skipped them. The rule is now structural rather than
 * incidental. (A shortfall cannot have `expected` 0 — `consumed < 0` is
 * impossible and the malformed-count branch already guards it — so this changes
 * nothing on that arm; it just stops the invariant depending on that being
 * remembered.)
 */
export function cursorAdvanceCounterKey(
  route: CursorAdvanceRoute,
  verdict: CursorAdvanceVerdict,
): string | null {
  if (verdict.expected === 0) return null;
  if (verdict.outcome === 'shortfall') {
    return cursorShortfallKey(route, verdict.reason, verdict.partial);
  }
  if (verdict.outcome === 'failed') {
    return cursorShortfallKey(route, 'failed', false);
  }
  return cursorShortfallKey(route, verdict.reported ? 'advanced' : 'advanced_unreported', false);
}

/** Context a site supplies so its log line identifies which of the nine it is. */
export interface CursorShortfallLogContext {
  route: CursorAdvanceRoute;
  /** Which call site — free text, e.g. `direct_chat.reply` or `opencode-reply`. */
  site: string;
  sessionId: string;
  /**
   * The local bookkeeping this site clears ANYWAY, despite the shortfall — e.g.
   * `['claim', 'markers']`. ENG-8346 deliberately changes no control flow, so
   * every site still clears exactly what it cleared before; recording WHAT was
   * cleared is what makes the deferred decision (ENG-8347) decidable rather than
   * a guess. An empty array is written as `none`, never omitted, so the field is
   * always present to grep for.
   */
  cleared: readonly string[];
}

/**
 * The one-line stderr/log warning for a shortfall.
 *
 * Deliberately a single line with `key=value` pairs: these land in a per-agent
 * log that is grepped, not read.
 */
export function formatCursorAdvanceShortfall(
  verdict: Extract<CursorAdvanceVerdict, { outcome: 'shortfall' }>,
  ctx: CursorShortfallLogContext,
): string {
  const cleared = ctx.cleared.length > 0 ? ctx.cleared.join(',') : 'none';
  return (
    `[direct-chat] cursor advance shortfall route=/${ctx.route} site=${ctx.site} ` +
    `session=${ctx.sessionId} expected=${verdict.expected} consumed=${verdict.consumed} ` +
    `reason=${verdict.reason} partial=${verdict.partial} cleared_anyway=${cleared}`
  );
}
